import { motion } from "framer-motion";
import { MapPin, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

const LocationWidget = () => {
  const { language } = useLanguage();

  const content = language === "en" ? {
    title: "Find Us",
    address: "Amyndas 18, Thessaloniki",
    phone: "2310 270290",
    hours: "24/7 Service",
    directions: "Get Directions",
    mapTitle: "LIFTCO Location"
  } : {
    title: "Βρείτε Μας",
    address: "Αμύντα 18, Θεσσαλονίκη",
    phone: "2310 270290",
    hours: "24ωρη Εξυπηρέτηση",
    directions: "Οδηγίες",
    mapTitle: "LIFTCO Τοποθεσία"
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            {content.title}
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 items-stretch">
          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 group"
          >
            <div className="h-full min-h-[350px] rounded-2xl overflow-hidden shadow-lg transition-shadow duration-300 group-hover:shadow-2xl">
              <div className="h-full w-full transition-transform duration-500 ease-out group-hover:scale-105">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3027.8!2d22.9487!3d40.6425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14a838f4a3b9c9e7%3A0x1234567890abcdef!2sAmynta%2018%2C%20Thessaloniki!5e0!3m2!1sen!2sgr!4v1703700000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '350px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={content.mapTitle}
                />
              </div>
            </div>
          </motion.div>

          {/* Info Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-card rounded-2xl p-8 shadow-lg flex flex-col justify-between"
          >
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-primary/10 text-primary">
                  <MapPin className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg text-foreground mb-1">
                    {language === "en" ? "Address" : "Διεύθυνση"}
                  </h3>
                  <p className="text-muted-foreground">{content.address}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-primary/10 text-primary">
                  <Phone className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg text-foreground mb-1">
                    {language === "en" ? "Phone" : "Τηλέφωνο"}
                  </h3>
                  <p className="text-muted-foreground">{content.phone}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-primary/10 text-primary">
                  <Clock className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg text-foreground mb-1">
                    {language === "en" ? "Hours" : "Ώρες"}
                  </h3>
                  <p className="text-muted-foreground">{content.hours}</p>
                </div>
              </div>
            </div>

            <Button asChild size="lg" className="w-full mt-8">
              <a
                href="https://maps.google.com/?q=Αμύντα+18+Θεσσαλονίκη"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MapPin className="h-5 w-5" />
                {content.directions}
              </a>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LocationWidget;
